import React, { useEffect, useState } from "react";
import { PageHeader } from "./Pengumuman";
import { Users, Search, Home, Edit3, User, Mail, Phone, IdCard, FileText, FileSpreadsheet, FileType, Upload } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { blokAPI, wargaInfoAPI } from "../lib/api";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { exportToPDF, exportToExcel, exportToWord, importFromExcel } from "../lib/exportUtils";

const emptyForm = { blok: "", nama: "", status_kepemilikan: "Pemilik", alamat: "", email: "", phone: "", ktp: "", kk: "", catatan: "" };

const formatIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n || 0);

const blokSort = (a, b) => {
  const pa = a.blok; const pb = b.blok;
  const [la, la_num] = [pa[0], parseInt(pa.replace(/[^0-9]/g, "") || 0, 10)];
  const [lb, lb_num] = [pb[0], parseInt(pb.replace(/[^0-9]/g, "") || 0, 10)];
  if (la !== lb) return la.localeCompare(lb);
  return la_num - lb_num;
};

const DataWarga = () => {
  const { user } = useApp();
  const canEdit = user.role === "admin" || user.role === "super_admin";
  const [bloks, setBloks] = useState([]);
  const [warga, setWarga] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    blokAPI.list().then(setBloks).catch(() => {});
    wargaInfoAPI.list().then(setWarga).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const wargaByBlok = warga.reduce((acc, w) => { acc[w.blok] = w; return acc; }, {});
  const rows = [...bloks].sort(blokSort).map((b) => ({
    ...b,
    info: wargaByBlok[b.blok] || {},
  }));

  const allBlokWithData = rows.map((r) => ({ blok: r.blok, ...r.info }));

  const filtered = rows.filter((r) => {
    const okQ = !q || r.blok.toLowerCase().includes(q.toLowerCase()) || (r.info?.nama || "").toLowerCase().includes(q.toLowerCase());
    const has = !!r.info?.nama;
    const okS = statusFilter === "all" || (statusFilter === "terisi" ? has : !has);
    return okQ && okS;
  });

  const stats = {
    total: rows.length,
    terisi: rows.filter((r) => r.info?.nama).length,
  };
  stats.kosong = stats.total - stats.terisi;

  const openEdit = (row) => {
    setForm({
      blok: row.blok,
      nama: row.info?.nama || "",
      status_kepemilikan: row.info?.status_kepemilikan || "Pemilik",
      alamat: row.info?.alamat || "",
      email: row.info?.email || "",
      phone: row.info?.phone || "",
      ktp: row.info?.ktp || "",
      kk: row.info?.kk || "",
      catatan: row.info?.catatan || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      await wargaInfoAPI.upsert(form);
      toast.success(`Data warga Blok ${form.blok} disimpan`);
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div>
      <PageHeader title="Data Warga" subtitle="Data pemilik/kontrak rumah per blok di DM Residence" icon={Users}
        action={<div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => {
            const rows = allBlokWithData.map((b, i) => [i + 1, b.blok, b.nama || "-", b.status_kepemilikan || "-", b.phone || "-", b.email || "-", b.ktp || "-", b.catatan || "-"]);
            exportToPDF({ filename: "data-warga.pdf", title: "DAFTAR DATA WARGA", orgName: "WARGA RT DM RESIDENCE",
              headers: ["No", "Blok", "Nama", "Status", "HP/WA", "Email", "KTP", "Catatan"], rows,
              summary: [["Total Blok", allBlokWithData.length], ["Terisi", allBlokWithData.filter(b => b.nama).length], ["Belum Terisi", allBlokWithData.filter(b => !b.nama).length]],
              orientation: "landscape" });
            toast.success("PDF diunduh");
          }} data-testid="dw-export-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
          <Button variant="outline" size="sm" onClick={() => {
            const rows = allBlokWithData.map((b, i) => [i + 1, b.blok, b.nama, b.status_kepemilikan, b.phone, b.email, b.ktp, b.kk, b.alamat, b.catatan]);
            exportToExcel({ filename: "data-warga.xlsx", sheetName: "Data Warga",
              headers: ["No", "Blok", "Nama", "Status", "HP/WA", "Email", "KTP", "KK", "Alamat", "Catatan"], rows });
            toast.success("Excel diunduh");
          }} data-testid="dw-export-excel"><FileSpreadsheet className="h-4 w-4 mr-1" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => {
            const rows = allBlokWithData.map((b, i) => [i + 1, b.blok, b.nama || "-", b.status_kepemilikan || "-", b.phone || "-", b.email || "-"]);
            exportToWord({ filename: "data-warga.doc", title: "DAFTAR DATA WARGA",
              headers: ["No", "Blok", "Nama", "Status", "HP/WA", "Email"], rows });
            toast.success("Word diunduh");
          }} data-testid="dw-export-word"><FileType className="h-4 w-4 mr-1" />Word</Button>
          {canEdit && <>
            <input type="file" accept=".xlsx,.xls,.csv" id="dw-import" className="hidden" onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              try {
                const rows = await importFromExcel(f);
                if (!rows.length) { toast.error("File kosong"); return; }
                if (!window.confirm(`Import ${rows.length} baris ke Data Warga? Data existing dengan blok yang sama akan di-update.`)) return;
                let ok = 0, fail = 0;
                for (const r of rows) {
                  const blok = String(r.blok || r.Blok || r.BLOK || "").trim();
                  if (!blok) { fail++; continue; }
                  const doc = {
                    blok, nama: String(r.nama || r.Nama || "").trim(),
                    status_kepemilikan: String(r.status_kepemilikan || r.Status || "Pemilik").trim() || "Pemilik",
                    phone: String(r.phone || r.HP || r.WA || r.telp || "").replace(/\s+/g, ""),
                    email: String(r.email || r.Email || "").trim(),
                    ktp: String(r.ktp || r.KTP || "").trim(),
                    kk: String(r.kk || r.KK || "").trim(),
                    alamat: String(r.alamat || r.Alamat || "").trim(),
                    catatan: String(r.catatan || r.Catatan || "").trim(),
                  };
                  try { await wargaInfoAPI.upsert(doc); ok++; } catch { fail++; }
                }
                toast.success(`Import selesai: ${ok} sukses, ${fail} gagal`);
                load();
              } catch (err) { toast.error(err.message || "File tidak valid"); }
              e.target.value = "";
            }} />
            <Label htmlFor="dw-import" className="cursor-pointer" data-testid="dw-import-label">
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-semibold h-8"><Upload className="h-3.5 w-3.5" />Import Excel</span>
            </Label>
          </>}
        </div>}
      />

      <div className="grid md:grid-cols-3 gap-4 mb-5">
        <Card className="border-emerald-100"><CardContent className="p-5"><div className="text-xs text-slate-500 font-semibold">TOTAL BLOK</div><div className="text-2xl font-extrabold text-slate-800 mt-1">{stats.total}</div></CardContent></Card>
        <Card className="border-emerald-100"><CardContent className="p-5"><div className="text-xs text-slate-500 font-semibold">SUDAH TERISI DATA</div><div className="text-2xl font-extrabold text-emerald-700 mt-1">{stats.terisi}</div></CardContent></Card>
        <Card className="border-amber-100"><CardContent className="p-5"><div className="text-xs text-slate-500 font-semibold">BELUM DIISI</div><div className="text-2xl font-extrabold text-amber-600 mt-1">{stats.kosong}</div></CardContent></Card>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Cari blok / nama pemilik..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Status</SelectItem><SelectItem value="terisi">Sudah Terisi</SelectItem><SelectItem value="kosong">Belum Diisi</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {filtered.map((r) => {
          const has = !!r.info?.nama;
          return (
            <Card key={r.blok} className={`card-hover overflow-hidden ${!has ? "border-dashed border-2 border-slate-200 bg-slate-50/30" : ""}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-11 w-11 rounded-xl brand-gradient text-white grid place-items-center font-black text-sm shadow" style={{ fontFamily: "Poppins, sans-serif" }}>{r.blok}</div>
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase font-semibold">Iuran K3</div>
                      <div className="text-xs font-bold brand-text">{formatIDR(r.nominal)}</div>
                    </div>
                  </div>
                  {canEdit && <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="h-8 w-8 p-0"><Edit3 className="h-3.5 w-3.5" /></Button>}
                </div>
                {has ? (
                  <div className="space-y-1 text-xs text-slate-700 border-t border-slate-100 pt-2">
                    <div className="flex items-center gap-1.5"><User className="h-3 w-3 text-slate-400" /><span className="font-semibold truncate">{r.info.nama}</span></div>
                    <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 text-[10px]">{r.info.status_kepemilikan || "Pemilik"}</Badge>
                    {r.info.phone && <div className="flex items-center gap-1.5 text-slate-500"><Phone className="h-3 w-3" /><span className="truncate">{r.info.phone}</span></div>}
                    {r.info.email && <div className="flex items-center gap-1.5 text-slate-500"><Mail className="h-3 w-3" /><span className="truncate">{r.info.email}</span></div>}
                    {r.info.ktp && <div className="flex items-center gap-1.5 text-slate-500"><IdCard className="h-3 w-3" /><span className="truncate">KTP: {r.info.ktp}</span></div>}
                  </div>
                ) : (
                  <div className="text-center py-3 text-xs text-slate-400 italic border-t border-slate-100 mt-2">Data belum diisi</div>
                )}
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && <div className="col-span-full text-center py-10 text-slate-400">Tidak ada blok sesuai filter</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Data Warga Blok {form.blok}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nama Lengkap</Label><Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Nama pemilik/penghuni" /></div>
              <div><Label>Status</Label><Select value={form.status_kepemilikan} onValueChange={(v) => setForm({ ...form, status_kepemilikan: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Pemilik">Pemilik</SelectItem><SelectItem value="Kontrak">Kontrak</SelectItem><SelectItem value="Kosong">Kosong / Belum Dihuni</SelectItem></SelectContent></Select></div>
            </div>
            <div><Label>Alamat Lengkap (KTP)</Label><Textarea rows={2} value={form.alamat} onChange={(e) => setForm({ ...form, alamat: e.target.value })} placeholder="Alamat sesuai KTP jika berbeda" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="nama@email.com" /></div>
              <div><Label>No. Kontak / WA</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="0812-xxxx-xxxx" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>No. KTP</Label><Input value={form.ktp} onChange={(e) => setForm({ ...form, ktp: e.target.value })} placeholder="16 digit" /></div>
              <div><Label>No. KK</Label><Input value={form.kk} onChange={(e) => setForm({ ...form, kk: e.target.value })} placeholder="16 digit" /></div>
            </div>
            <div><Label>Catatan (opsional)</Label><Textarea rows={2} value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} placeholder="Info tambahan" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={submit} className="brand-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default DataWarga;
